/**
 * DashboardSkeleton - Fast loading skeleton for dashboard
 */

import { Skeleton } from '../../../components/ui/skeleton';
import { Card } from '../../../components/ui/card';
import { Container, Stack } from '../../../components/primitives';

export function DashboardSkeleton() {
  return (
    <Container size="large" className="py-6 md:py-8">
      <Stack spacing="large">
        {/* Header Skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-80" />
        </div>

        {/* Selected Companies Indicator Skeleton */}
        <Skeleton className="h-10 w-full max-w-sm" />

        {/* Stats Cards - SECTION 1 */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-4 md:p-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-8 w-8 md:h-10 md:w-10 rounded-full" />
                </div>
                <Skeleton className="h-7 md:h-8 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </Card>
          ))}
        </div>

        {/* Budget Overview - SECTION 2 (Full Width) */}
        <Card className="p-4 md:p-6">
          <div className="space-y-4">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-16 w-full" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          </div>
        </Card>

        {/* Recent Transactions - SECTION 3 (Full Width) */}
        <Card className="p-4 md:p-6">
          <div className="space-y-3">
            <Skeleton className="h-6 w-44" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border">
                <Skeleton className="h-5 w-5 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <div className="space-y-1 shrink-0">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Alerts + Category Status - SECTION 4 */}
        <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
          <Card className="p-4 md:p-6">
            <div className="space-y-3">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-20 w-full" />
            </div>
          </Card>
          <Card className="p-4 md:p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-44" />
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-2 w-full" />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Charts - SECTION 5 */}
        <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
          <Card className="p-4 md:p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-56" />
              <Skeleton className="h-[300px] w-full" />
            </div>
          </Card>
          <Card className="p-4 md:p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-56" />
              <Skeleton className="h-[300px] w-full" />
            </div>
          </Card>
        </div>
      </Stack>
    </Container>
  );
}
